import React from 'react';
import { type EspecieRequest, type EspecieFormErrors } from '../types/especie';
import { EspecieValidation } from '../services/especieValidation';
import { EspecieService } from '../services/especieService';

interface UseEspecieFormReturn {
  formData: EspecieRequest;
  errors: EspecieFormErrors;
  loading: boolean;
  isValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSubmit: () => Promise<void>;
  resetForm: () => void;
}

export const useEspecieForm = (
  especieId: number | null,
  onSave: (especie: EspecieRequest) => void
): UseEspecieFormReturn => {
  const [formData, setFormData] = React.useState<EspecieRequest>({
    nombreComun: '',
    nombreCientifico: '',
  });
  const [errors, setErrors] = React.useState<EspecieFormErrors>({});
  const [loading, setLoading] = React.useState(false);

  const isValid = React.useMemo(() => {
    return EspecieValidation.isValidEspecie(formData);
  }, [formData]);

  React.useEffect(() => {
    const loadEspecie = async () => {
      if (especieId) {
        setLoading(true);
        try {
          const especie = await EspecieService.getEspecieById(especieId);
          setFormData({
            idEspecie: especie.idEspecie,
            nombreComun: especie.nombreComun,
            nombreCientifico: especie.nombreCientifico,
          });
        } catch (error) {
          console.error('Error loading especie:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    loadEspecie();
  }, [especieId]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear specific error when user starts typing
    if (errors[name as keyof EspecieFormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async () => {
    const validationErrors = EspecieValidation.validateEspecie(formData);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length === 0) {
      setLoading(true);
      try {
        await onSave(formData);
      } catch (error) {
        console.error('Error saving especie:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const resetForm = React.useCallback(() => {
    setFormData({
      nombreComun: '',
      nombreCientifico: '',
    });
    setErrors({});
  }, []);

  return {
    formData,
    errors,
    loading,
    isValid,
    handleChange,
    handleSubmit,
    resetForm,
  };
};